import React, { forwardRef } from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { UI_INVARIANTS } from '../../config/ui-invariants';

/**
 * CONTRACT UI PRIMITIVES
 * Authority: Visual Law (ui-invariants.js)
 * 
 * These components enforce strict adherence to the system's visual contract.
 * Usage of raw HTML/Tailwind for these elements in Task Views is FORBIDDEN.
 */

// --- CONTRACT CARD ---
// Enforces: UI_CARD (Padding, Border, Shadow, Height)
export const ContractCard = forwardRef(({ children, className, hoverable = true, ...props }, ref) => {
    return (
        <Card
            ref={ref}
            className={cn(
                // Base Structure (Tailwind mappings of Invariants)
                "bg-white border text-card-foreground shadow-sm", // Matches UI_CARD.BACKGROUND/BORDER (implicit via component)

                // Enforced Layout (No overrides allowed for these)
                "p-6", // UI_CARD.PADDING (1.5rem)
                "rounded-lg", // UI_CARD.BORDER_RADIUS (0.5rem)
                "min-h-[200px]", // UI_CARD.MIN_HEIGHT

                // Interaction
                hoverable && "hover:shadow-md transition-all duration-200 hover:border-brand-300",

                // External utility injection (Limited scope - mainly for margins)
                className
            )}
            {...props}
        >
            {children}
        </Card>
    );
});
ContractCard.displayName = "ContractCard";

// --- CONTRACT TEXT ---
// Enforces: UI_TYPOGRAPHY
export const ContractText = ({ variant = "body", children, className, as = "p" }) => {
    // Map variant to Invariant Constants
    const styleMap = {
        cardTitle: UI_INVARIANTS.TYPOGRAPHY.CARD_TITLE,
        detailTitle: UI_INVARIANTS.TYPOGRAPHY.DETAIL_TITLE,
        sectionLabel: UI_INVARIANTS.TYPOGRAPHY.SECTION_LABEL,
        body: UI_INVARIANTS.TYPOGRAPHY.BODY_TEXT,
        caption: UI_INVARIANTS.TYPOGRAPHY.CAPTION,
        stat: UI_INVARIANTS.TYPOGRAPHY.STAT_NUMBER
    };

    const enforcedClass = styleMap[variant] || styleMap.body;
    const Component = as;

    return (
        <Component className={cn(enforcedClass, className)}>
            {children}
        </Component>
    );
};

// --- CONTRACT BADGE ---
// Enforces: DESIGN_INTENTS (Badges)
export const ContractBadge = ({ intent = "neutral", children, className }) => {
    // Intents map to specific color tokens
    const variants = {
        neutral: "bg-slate-100 text-slate-700 border-slate-200", // Default
        success: "bg-green-100 text-green-700 border-green-200", // Matches SUCCESS_BG/TEXT
        error: "bg-red-100 text-red-700 border-red-200", // Matches ERROR_BG/TEXT
        warning: "bg-amber-100 text-amber-800 border-amber-200",
        info: "bg-brand-50 text-brand-700 border-brand-100",
        destructive: "bg-destructive/10 text-destructive border-destructive/20" // High Priority
    };

    return (
        <span className={cn(
            "inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide border",
            variants[intent] || variants.neutral,
            className
        )}>
            {children}
        </span>
    );
};

// --- CONTRACT BUTTON ---
// Enforces: DESIGN_INTENTS (Buttons), UI_ACTIONS
export const ContractButton = ({ intent = "positive", variant = "primary", children, className, disabled, onContractClick, ...props }) => {
    // CANONICAL INTENT MEANINGS:
    // - brand: Brand identity ONLY (#12125A / brand-900) - logo, headers, brand moments
    // - positive: Positive actions (#12125A / action) - Approve, Accept, Send, Save
    // - success/error/warning: Status feedback
    // - ghost/outline: Neutral actions
    //
    // DEFAULT: 'positive' (most buttons are actions, not brand identity)

    // Intents map to specific color tokens
    const intentClasses = {
        brand: "bg-brand-900 hover:bg-brand-950 text-white border-brand-950 shadow-sm",  // #12125A - Brand identity ONLY
        positive: "bg-action hover:bg-action-hover text-white border-action-hover shadow-sm",  // #1919A8 - Positive actions (Approve, Accept, Send)
        success: "bg-brand-900 hover:bg-brand-dark text-white border-brand-900 shadow-sm",
        error: "bg-white text-red-600 border-slate-200 hover:border-brand-900",
        warning: "bg-white text-slate-600 border-slate-200 hover:border-brand-900 hover:text-brand-900",
        ghost: "bg-transparent hover:bg-slate-100 text-slate-600 border-transparent",
        outline: "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm"
    };

    return (
        <button
            disabled={disabled}
            className={cn(
                "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md font-heading text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 border",
                intentClasses[intent] || intentClasses.positive,  // Default to positive, not brand
                disabled && "opacity-50 cursor-not-allowed shadow-none",
                className
            )}
            onClick={(e) => {
                if (onContractClick) {
                    onContractClick(e);
                }
            }}
            {...props}
        >
            {children}
        </button>
    );
};

// --- CONTRACT MODAL CONTAINER ---
// Enforces: UI_DETAIL_VIEW (Width, Background)
export const ContractModalContainer = ({ children, onClose }) => {
    return (
        <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{ backgroundColor: UI_INVARIANTS.COLORS.OVERLAY }} /* noscan — token-driven via UI_INVARIANTS */
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                style={{ maxWidth: '800px' }}
                onClick={e => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
};

// --- CONTRACT CARD ACTIONS ---
// Enforces: Class 1 Integrity (Encapsulates ad-hoc logic)
export const ContractCardActions = ({ actions = [], className }) => {
    return (
        <div className={cn("flex gap-2 mt-4", className)}>
            {actions.filter(a => !a.hidden).map((action, idx) => (
                <ContractButton
                    key={idx}
                    intent={action.intent || (action.variant === 'destructive' ? 'error' : 'brand')}
                    className={cn("flex-1", action.className)}
                    disabled={action.disabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        action.onClick();
                    }}
                >
                    {action.icon}
                    {action.label}
                </ContractButton>
            ))}
        </div>
    );
};
